import React from 'react';

import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';

import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {SentinelLoading} from 'presentation/components';
import AutocompleteMobileOptions from 'presentation/components/Filter/components/AutocompleteMobileOptions';
import {BookingErrorState} from 'presentation/modules/booking/shared/ui/components/BookingErrorState';
import {BookingLoadingState} from 'presentation/modules/booking/shared/ui/components/BookingLoadingState';
import {ProfessionalDateList} from 'presentation/modules/booking/shared/ui/components/ProfessionalSelection/ProfessionalDateList';
import {ProfessionalDateSection} from 'presentation/modules/booking/shared/ui/components/ProfessionalSelection/ProfessionalDateSection';
import {PreviousProviderCard} from 'presentation/modules/booking/shared/ui/components/ProfessionalSelection/PreviousProviderCard';
import {translations} from 'presentation/translations-new';
import {HealthProfessionalSelectionNotFoundState} from '../HealthProfessionalSelectionNotFoundState';
import {useHealthNearestDateProfessionalList} from './hooks/useHealthNearestDateProfessionalList';
import {HealthNearestDateProfessionalListProps} from './types';

const {bookHealth} = translations['pt-br'].privateFlow.services;
const {loadingState, loadingError} = translations['pt-br'].privateFlow.bookingFlow.professionalFlow;

export function HealthNearestDateProfessionalList({
	handleSelectHour,
	handleClearShiftAndSearch,
	availableDates,
	filteredProvidersNameOptions,
}: HealthNearestDateProfessionalListProps) {
	const {
		isLoading,
		isError,
		healthMultiAvailableTimesRefetch,
		showNotFoundState,
		shouldShowMobileOptions,
		shouldShowAvailableTimesList,
		isAutocompleteFocusedState,
		autocompleteQuery,
		isSentinelOnVisibleActive,
		loadMoreDates,
		isSentinelLoading,
		shouldNotUseHighlight,
		query,
		setCurrentValueInputTypingState,
		previousProviderData,
		timesWithoutPreviousProvider,
		showPreviousProvider,
	} = useHealthNearestDateProfessionalList({availableDates, filteredProvidersNameOptions});

	if (isLoading) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={bookHealth}
				loadingText={loadingState}
			/>
		);
	}

	if (isError) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={bookHealth}
				errorTitle={loadingError}
				handleRefresh={healthMultiAvailableTimesRefetch}
			/>
		);
	}

	if (showNotFoundState) {
		return (
			<HealthProfessionalSelectionNotFoundState
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_HORARIOS_AGENDAMENTO_DISPONIVEL}
				showClearFilterButton
				handleClearShiftAndSearch={() => {
					handleClearShiftAndSearch();
					setCurrentValueInputTypingState('');
				}}
			/>
		);
	}

	return (
		<ProfessionalDateList>
			{shouldShowMobileOptions ? (
				<AutocompleteMobileOptions options={filteredProvidersNameOptions} />
			) : null}

			{showPreviousProvider && previousProviderData ? (
				<PreviousProviderCard
					flowType={FlowEnum.HEALTH}
					professionalCode={previousProviderData.professionalCode}
					professionalName={previousProviderData.professionalName}
					dates={previousProviderData.dates}
					recommendations={previousProviderData.recommendations}
					onSelectHour={handleSelectHour}
					isFavorite={previousProviderData.isFavorite}
				/>
			) : null}

			{shouldShowAvailableTimesList
				? timesWithoutPreviousProvider?.map(timesByDate => {
						if (timesByDate.times.length === 0) return null;

						return (
							<ProfessionalDateSection
								key={timesByDate.date}
								date={timesByDate.date}
								professionals={timesByDate.times}
								onSelectHour={handleSelectHour}
								flowType={FlowEnum.HEALTH}
								query={query}
								shouldNotUseHighlight={shouldNotUseHighlight}
							/>
						);
					})
				: null}

			{!isAutocompleteFocusedState && autocompleteQuery ? (
				<SentinelLoading
					minHeight="50px"
					onVisible={() => {
						if (isSentinelOnVisibleActive) {
							loadMoreDates();
						}
					}}
					isLoading={isSentinelLoading}
					sx={{marginTop: 4}}
				/>
			) : null}
		</ProfessionalDateList>
	);
}
